import { useOverlayState } from "@heroui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	type CommunityLink,
	type CreateCommunityLinkPayload,
	createCommunityLinkSchema,
	type UpdateCommunityLinkPayload,
	updateCommunityLinkSchema,
} from "@upsquad/shared";
import { Check, ExternalLink, Pencil, Trash2, X } from "lucide-react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button, FieldError, Input, Label, Modal, Spinner, TextField } from "@/src/common/components/ui";
import { useAppSelector } from "@/src/common/redux/store";
import { toastService } from "@/src/common/services/toast.service";
import { getActiveCommunityId } from "@/src/community/store/community-selectors";
import {
	useCreateCommunityLinkMutation,
	useDeleteCommunityLinkMutation,
	useListCommunityLinksQuery,
	useUpdateCommunityLinkMutation,
} from "../services/community-link-http.service";

interface ManageLinksModalProps {
	isOpen: boolean;
	onClose: () => void;
}

export function ManageLinksModal({ isOpen, onClose }: ManageLinksModalProps) {
	const { t } = useTranslation("communityLink");
	const communityId = useAppSelector(getActiveCommunityId);
	const state = useOverlayState({ isOpen, onOpenChange: (open) => !open && onClose() });

	const { data, isLoading: isListLoading } = useListCommunityLinksQuery(communityId ? { communityId } : { communityId: "" }, {
		skip: !communityId,
	});
	const links = data?.links ?? [];

	return (
		<Modal state={state}>
			<Modal.Backdrop>
				<Modal.Container>
					<Modal.Dialog className="max-w-lg">
						<Modal.Header>
							<h2 className="text-lg font-semibold">{t("modal_title")}</h2>
						</Modal.Header>
						<Modal.Body className="space-y-4 p-0.5">
							<CreateLinkForm communityId={communityId} />
							<div className="border-t border-default-200 pt-4">
								{isListLoading ? (
									<div className="flex justify-center py-4">
										<Spinner />
									</div>
								) : links.length === 0 ? (
									<p className="text-center text-sm text-foreground/60">{t("empty")}</p>
								) : (
									<ul className="space-y-1">
										{links.map((link) => (
											<LinkRow key={link.id} link={link} communityId={communityId} />
										))}
									</ul>
								)}
							</div>
						</Modal.Body>
						<Modal.Footer className="flex justify-end">
							<Button variant="ghost" onPress={onClose}>
								{t("actions.cancel")}
							</Button>
						</Modal.Footer>
					</Modal.Dialog>
				</Modal.Container>
			</Modal.Backdrop>
		</Modal>
	);
}

interface CreateLinkFormProps {
	communityId: string | null;
}

function CreateLinkForm({ communityId }: CreateLinkFormProps) {
	const { t } = useTranslation("communityLink");
	const [createLink, { isLoading }] = useCreateCommunityLinkMutation();

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<CreateCommunityLinkPayload>({
		resolver: zodResolver(createCommunityLinkSchema),
		defaultValues: { title: "", url: "" },
	});

	const onSubmit = async (payload: CreateCommunityLinkPayload) => {
		if (!communityId) return;
		try {
			await createLink({ communityId, payload }).unwrap();
			toastService.success(t("toast.created"));
			reset();
		} catch {
			toastService.error(t("errors.create_failed"));
		}
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
			<Controller
				name="title"
				control={control}
				render={({ field }) => (
					<TextField isInvalid={!!errors.title} isRequired>
						<Label>{t("form.title_label")}</Label>
						<Input
							value={field.value ?? ""}
							onChange={field.onChange}
							onBlur={field.onBlur}
							name={field.name}
							placeholder={t("form.title_placeholder")}
						/>
						{errors.title?.message && <FieldError>{errors.title.message}</FieldError>}
					</TextField>
				)}
			/>
			<Controller
				name="url"
				control={control}
				render={({ field }) => (
					<TextField isInvalid={!!errors.url} isRequired>
						<Label>{t("form.url_label")}</Label>
						<Input
							type="url"
							value={field.value ?? ""}
							onChange={field.onChange}
							onBlur={field.onBlur}
							name={field.name}
							placeholder={t("form.url_placeholder")}
						/>
						{errors.url?.message && <FieldError>{errors.url.message}</FieldError>}
					</TextField>
				)}
			/>
			<div className="flex justify-end">
				<Button type="submit" variant="primary" isPending={isLoading} isDisabled={!communityId}>
					{t("actions.add")}
				</Button>
			</div>
		</form>
	);
}

interface LinkRowProps {
	link: CommunityLink;
	communityId: string | null;
}

function LinkRow({ link, communityId }: LinkRowProps) {
	const { t } = useTranslation("communityLink");
	const [mode, setMode] = useState<"view" | "edit" | "confirmDelete">("view");
	const [updateLink, { isLoading: isUpdating }] = useUpdateCommunityLinkMutation();
	const [deleteLink, { isLoading: isDeleting }] = useDeleteCommunityLinkMutation();

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<UpdateCommunityLinkPayload>({
		resolver: zodResolver(updateCommunityLinkSchema),
		defaultValues: { title: link.title, url: link.url },
	});

	const cancelEdit = () => {
		reset({ title: link.title, url: link.url });
		setMode("view");
	};

	const onSubmit = async (payload: UpdateCommunityLinkPayload) => {
		if (!communityId) return;
		try {
			await updateLink({ communityId, linkId: link.id, payload }).unwrap();
			toastService.success(t("toast.updated"));
			setMode("view");
		} catch {
			toastService.error(t("errors.update_failed"));
		}
	};

	const onDelete = async () => {
		if (!communityId) return;
		try {
			await deleteLink({ communityId, linkId: link.id }).unwrap();
			toastService.success(t("toast.deleted"));
		} catch {
			toastService.error(t("errors.delete_failed"));
		}
	};

	if (mode === "edit") {
		return (
			<li>
				<form onSubmit={handleSubmit(onSubmit)} className="space-y-2 rounded-lg border border-default-200 p-3">
					<Controller
						name="title"
						control={control}
						render={({ field }) => (
							<TextField isInvalid={!!errors.title}>
								<Label>{t("form.title_label")}</Label>
								<Input value={field.value ?? ""} onChange={field.onChange} onBlur={field.onBlur} name={field.name} />
								{errors.title?.message && <FieldError>{errors.title.message}</FieldError>}
							</TextField>
						)}
					/>
					<Controller
						name="url"
						control={control}
						render={({ field }) => (
							<TextField isInvalid={!!errors.url}>
								<Label>{t("form.url_label")}</Label>
								<Input type="url" value={field.value ?? ""} onChange={field.onChange} onBlur={field.onBlur} name={field.name} />
								{errors.url?.message && <FieldError>{errors.url.message}</FieldError>}
							</TextField>
						)}
					/>
					<div className="flex justify-end gap-2">
						<Button type="button" variant="ghost" onPress={cancelEdit}>
							{t("actions.cancel")}
						</Button>
						<Button type="submit" variant="primary" isPending={isUpdating}>
							{t("actions.save")}
						</Button>
					</div>
				</form>
			</li>
		);
	}

	if (mode === "confirmDelete") {
		return (
			<li className="flex items-center gap-2 rounded-lg border border-danger/40 bg-danger/5 p-2">
				<span className="flex-1 truncate text-sm">{t("delete_confirm")}</span>
				<button
					type="button"
					onClick={() => setMode("view")}
					className="rounded-md p-1.5 text-default-400 hover:bg-default-100"
					aria-label={t("actions.cancel")}
				>
					<X size={16} />
				</button>
				<button
					type="button"
					onClick={onDelete}
					disabled={isDeleting}
					className="rounded-md bg-danger p-1.5 text-white hover:bg-danger/90 disabled:opacity-60"
					aria-label={t("actions.delete")}
				>
					<Check size={16} />
				</button>
			</li>
		);
	}

	return (
		<li className="flex items-center gap-2 rounded-lg p-2 hover:bg-default-100">
			<ExternalLink size={16} className="shrink-0 text-foreground/60" />
			<div className="flex min-w-0 flex-1 flex-col">
				<span className="truncate text-sm font-medium">{link.title}</span>
				<span className="truncate text-xs text-foreground/60">{link.url}</span>
			</div>
			<button
				type="button"
				onClick={() => setMode("edit")}
				className="rounded-md p-1.5 text-default-400 hover:bg-default-200 hover:text-foreground"
				aria-label={t("actions.edit")}
			>
				<Pencil size={14} />
			</button>
			<button
				type="button"
				onClick={() => setMode("confirmDelete")}
				className="rounded-md p-1.5 text-default-400 hover:bg-danger-50 hover:text-danger"
				aria-label={t("actions.delete")}
			>
				<Trash2 size={14} />
			</button>
		</li>
	);
}
